/*
Copyright 2026 Integrated Tech9labs Pvt. Ltd.
All Rights Reserved.

This file is part of Swift and is licensed under the GNU Affero General
Public License, version 3 (see the LICENSE file at the root of the
repository).
*/

/**
 * Clients (DR Phase 24; decisions 2026-10-05). Swift manages many clients, one project each.
 *
 * - DrOverview: every client's DR at a glance, for the provider's staff, worst first. "Open…" gives the staff
 *   member operator and DR operator roles on the client's project for a few hours, for a reason kept in the audit
 *   log, and switches into it.
 * - ClientAccessBanner: inside a client, the staff member sees until when, and leaves (the roles are taken back).
 */

import * as React from "react";
import styled from "styled-components";
import { DoorOpen, LogOut } from "lucide-react";

import { drErrorMessage } from "@src/components/modules/DrModule/drUtils";
import { ActionButton } from "@src/components/ui/DetailsKit";
import { Muted, Pill, T9, formatDateTime, timeAgo } from "@src/components/ui/Lists/ListTable";
import { Notice, OpsInput } from "@src/components/ui/OpsDialog";
import drSource from "@src/sources/DrSource";
import userStore from "@src/stores/UserStore";

import type { DrClientAccess, DrOverview, DrOverviewClient } from "@src/@types/Dr";

const Table = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1.6fr) repeat(6, minmax(90px, 1fr)) 110px;
  font-size: 13px;
  color: ${T9.slate700};
  > div {
    padding: 9px 10px;
    border-bottom: 1px solid ${T9.slate100};
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
  }
`;
const Head = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${T9.slate500};
`;
const Name = styled.span`
  font-weight: 600;
  color: ${T9.slate900};
`;
const Num = styled.span`
  font-variant-numeric: tabular-nums;
`;
const OpenForm = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  background: ${T9.slate50};
`;

const clientName = (c: DrOverviewClient) => c.name || c.project_id;

type OverviewProps = {
  /** Called once the portal has switched into the client's project. */
  onOpened: (client: DrOverviewClient) => void;
};
export const DrOverviewView = (props: OverviewProps) => {
  const [overview, setOverview] = React.useState<DrOverview | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [opening, setOpening] = React.useState<DrOverviewClient | null>(null);
  const [reason, setReason] = React.useState("");
  const [hours, setHours] = React.useState("8");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    drSource.overview().then(setOverview, err => setError(drErrorMessage(err, "Could not load the clients.")));
  }, []);

  const open = async () => {
    if (!opening) return;
    setBusy(true);
    setError(null);
    try {
      await drSource.openClientAccess(opening.project_id, reason.trim(), Number(hours));
      await userStore.switchProject(opening.project_id);
      props.onOpened(opening);
    } catch (err) {
      setError(drErrorMessage(err, "SwiftIQ did not open this client."));
    } finally {
      setBusy(false);
    }
  };

  if (!overview) return error ? <Notice tone="danger">{error}</Notice> : <Muted>Loading…</Muted>;
  return (
    <div data-test-id="dr-overview" style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {overview.clients.length === 0 ? (
        <Muted>No client has disaster recovery set up yet.</Muted>
      ) : (
        <Table>
          {["Client", "VMs", "Needs attention", "RPO", "Approvals", "Runs", "Last drill", ""].map(h => (
            <Head key={h}>{h}</Head>
          ))}
          {overview.clients.map(c => (
            <React.Fragment key={c.project_id}>
              <div data-test-id="dr-overview-client">
                <Name>{clientName(c)}</Name>
                {c.staff_inside.length ? (
                  <Muted>In it: {c.staff_inside.map(s => s.user_name).join(", ")}</Muted>
                ) : null}
              </div>
              <div>
                <Num>{c.protected} of {c.workloads}</Num>
                {c.at_dr ? <Muted>{c.at_dr} at the DR site</Muted> : null}
              </div>
              <div>{c.attention ? <Pill tone="danger">{c.attention}</Pill> : <Muted>None</Muted>}</div>
              <div>
                <Num>{c.rpo.MET} met</Num>
                {c.rpo.BREACHED ? <Muted>{c.rpo.BREACHED} missed</Muted> : null}
                {c.rpo.NO_POINT ? <Muted>{c.rpo.NO_POINT} no point yet</Muted> : null}
              </div>
              <div>{c.pending_approvals ? <Pill tone="warning">{c.pending_approvals} waiting</Pill> : <Muted>None</Muted>}</div>
              <div>
                {c.runs_in_progress ? <Num>{c.runs_in_progress} running</Num> : null}
                {c.runs_failed ? <Pill tone="danger">{c.runs_failed} failed</Pill> : null}
                {!c.runs_in_progress && !c.runs_failed ? <Muted>None</Muted> : null}
              </div>
              <div>
                {c.last_test_failover ? (
                  <span title={formatDateTime(c.last_test_failover)}>{timeAgo(c.last_test_failover)}</span>
                ) : (
                  <Muted>Never</Muted>
                )}
              </div>
              <div>
                <ActionButton
                  type="button"
                  variant="secondary"
                  onClick={() => setOpening(opening?.project_id === c.project_id ? null : c)}
                  data-test-id="dr-overview-open"
                >
                  <DoorOpen size={16} /> Open…
                </ActionButton>
              </div>
              {opening?.project_id === c.project_id ? (
                <OpenForm data-test-id="dr-overview-open-form">
                  <span>Work in {clientName(c)} as operator and DR operator for</span>
                  <OpsInput
                    value={hours}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHours(e.target.value)}
                    style={{ width: 60 }}
                    data-test-id="dr-overview-open-hours"
                  />
                  <span>hours, because</span>
                  <OpsInput
                    value={reason}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
                    placeholder="Reason (kept in the audit log)"
                    style={{ flex: 1, minWidth: 220 }}
                    data-test-id="dr-overview-open-reason"
                  />
                  <ActionButton type="button" onClick={open} disabled={busy || !reason.trim()} data-test-id="dr-overview-open-confirm">
                    {busy ? "Opening…" : "Open client"}
                  </ActionButton>
                </OpenForm>
              ) : null}
            </React.Fragment>
          ))}
        </Table>
      )}
    </div>
  );
};

type BannerProps = {
  /** Called once the portal has left the client (switched back to another project). */
  onLeft: () => void;
};
/** Inside a client: the staff member's own access to it, if any, and a way out. */
export const ClientAccessBanner = (props: BannerProps) => {
  const [access, setAccess] = React.useState<DrClientAccess | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    drSource.clientAccesses().then(found => setAccess(found[0] || null), () => setAccess(null));
  }, []);

  if (!access) return null;
  const leave = async () => {
    setBusy(true);
    setError(null);
    try {
      await drSource.closeClientAccess(access.id);
      await userStore.switchProject("");
      props.onLeft();
    } catch (err) {
      setError(drErrorMessage(err, "SwiftIQ did not close your access."));
      setBusy(false);
    }
  };
  return (
    <Notice tone="warning" title="You are working in this client" >
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }} data-test-id="dr-client-access-banner">
        <span>
          Until <span title={formatDateTime(access.expires_at)}>{formatDateTime(access.expires_at)}</span>, for: {access.reason}.
          SwiftIQ takes your roles back then.
        </span>
        <ActionButton type="button" variant="secondary" onClick={leave} disabled={busy} data-test-id="dr-client-access-leave">
          <LogOut size={16} /> {busy ? "Leaving…" : "Leave client"}
        </ActionButton>
        {error ? <span>{error}</span> : null}
      </div>
    </Notice>
  );
};
